import { AfterViewChecked, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { MapInfoWindow, MapMarker } from '@angular/google-maps';

interface RouteStopAddressLike {
  lat?: number | string | null;
  lng?: number | string | null;
  long?: number | string | null;
  city?: string;
  state?: string;
  address?: string;
}

interface RouteStopLike {
  stopageType?: number | string | null;
  location?: RouteStopAddressLike | null;
  addressInfo?: RouteStopAddressLike | null;
  addressId?: RouteStopAddressLike | null;
}

interface NormalizedRouteStop {
  stopageType: number;
  position: google.maps.LatLngLiteral;
  address: string;
}

@Component({
  selector: 'app-map-load-list',
  templateUrl: './map-load-list.component.html',
  styleUrl: './map-load-list.component.scss'
})
export class MapLoadListComponent implements OnInit, OnChanges, AfterViewChecked, OnDestroy {
  @Input() dataList: any[] = [];
  zoom = 3;
  polylineOptions: google.maps.PolylineOptions = {
    geodesic: true,
    strokeWeight: 4,
    strokeColor: '#0354acff',
    strokeOpacity: 0.6
  };
  infoOptions: google.maps.InfoWindowOptions & { headerDisabled: boolean } = {
    maxWidth: 340,
    headerDisabled: true
  };
  private activeInfoWindow?: MapInfoWindow;
  // Google Maps' fullscreen control puts the map div into the browser Fullscreen API,
  // so "expanded view" is simply whether a fullscreen element is currently active.
  private readonly onFullscreenChange = () => {
    if (!this.isMapExpanded()) {
      this.activeInfoWindow?.close();
      this.activeInfoWindow = undefined;
    }
  };

  constructor(
    private cdr: ChangeDetectorRef
  ) {
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);
  }

  ngOnDestroy() {
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);
  }

  // The marker popup is only shown in the expanded (fullscreen) map, not the small card.
  openInfo(marker: MapMarker, infoWindow: MapInfoWindow): void {
    if (!this.isMapExpanded()) {
      return;
    }
    this.activeInfoWindow?.close();
    infoWindow.open(marker);
    this.activeInfoWindow = infoWindow;
  }

  closeInfo(infoWindow: MapInfoWindow): void {
    infoWindow.close();
    if (this.activeInfoWindow === infoWindow) {
      this.activeInfoWindow = undefined;
    }
  }

  private isMapExpanded(): boolean {
    return !!(document.fullscreenElement || (document as any).webkitFullscreenElement);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['dataList'] && this.dataList?.length) {
      this.onMapShow(this.dataList);
    }
  }

  ngOnInit() {
    if (this.dataList?.length) {
      this.onMapShow(this.dataList);
    }
  }

onMapShow(dataList: any[]) {
  dataList.forEach((item) => {
    item.isLoading = true;
    item.showMap = false;
    item.mapCenter = null;

    const routeStops = this.getRouteStops(item);
    const pickupStops = routeStops.filter((stop) => this.isPickupStop(stop.stopageType));
    const dropStops = routeStops.filter((stop) => this.isDropStop(stop.stopageType));
    const originStop = pickupStops.length ? pickupStops[0] : null;
    const destinationStop = dropStops.length ? dropStops[dropStops.length - 1] : null;
    const originFromStops = originStop ? originStop.position : null;
    const destinationFromStops = destinationStop ? destinationStop.position : null;
    const originAddress = (originStop && originStop.address) || item.origin || '';
    const destinationAddress = (destinationStop && destinationStop.address) || item.destination || '';

    const oLat = Number(originFromStops?.lat ?? item.originCords?.lat);
    const oLng = Number(originFromStops?.lng ?? item.originCords?.lng);
    const dLat = Number(destinationFromStops?.lat ?? item.destinationCords?.lat);
    const dLng = Number(destinationFromStops?.lng ?? item.destinationCords?.lng);

    if (!Number.isFinite(oLat) || !Number.isFinite(oLng)) {
      item.isLoading = false;
      return;
    }

    if (pickupStops.length > 1) {
      item.origin = `Multiple Origins (${pickupStops.length})`;
    }
    if (dropStops.length > 1) {
      item.destination = `Multiple Destinations (${dropStops.length})`;
    }

    const origin: google.maps.LatLngLiteral = { lat: oLat, lng: oLng };
    const destination: google.maps.LatLngLiteral | null =
      Number.isFinite(dLat) && Number.isFinite(dLng) ? { lat: dLat, lng: dLng } : null;

    const waypointStops = routeStops.slice(1, Math.max(1, routeStops.length - 1));
    const waypointCoords = waypointStops.map((stop) => stop.position);
    const waypoints = waypointCoords.map((position) => ({ location: position, stopover: true }));

    const uniqueStops = routeStops.filter((stop, index, allStops) => {
      return allStops.findIndex((other) =>
        other.position.lat === stop.position.lat &&
        other.position.lng === stop.position.lng &&
        other.stopageType === stop.stopageType
      ) === index;
    });

    const middleMarkers = uniqueStops.slice(1, Math.max(1, uniqueStops.length - 1)).map((stop) => {
      const stopIsPickup = this.isPickupStop(stop.stopageType);
      const stopLabel = stopIsPickup ? 'Pick-up' : 'Drop-off';
      return {
        position: stop.position,
        options: this.markerOptions(stopIsPickup ? '#28a745' : '#d93025', stopLabel),
        label: stopLabel,
        isPickup: stopIsPickup,
        address: stop.address || ''
      };
    });

    const isSinglePoint =
      !destination || (oLat === dLat && oLng === dLng);
    if (isSinglePoint) {
      item.markers = [{
        position: origin,
        options: this.markerOptions('#28a745', 'Pick-up'),
        label: 'Pick-up',
        isPickup: true,
        address: originAddress
      }];
      item.polylinePath = null;
      item.mapCenter = origin;
      this.zoom = 7;
      item.showMap = true;
      setTimeout(() => {
        item.isLoading = false;
        this.cdr.detectChanges();
      });

      return;
    }

    item.markers = [
      { position: origin, options: this.markerOptions('#28a745', 'Pick-up'), label: 'Pick-up', isPickup: true, address: originAddress },
      ...middleMarkers,
      { position: destination, options: this.markerOptions('#d93025', 'Drop-off'), label: 'Drop-off', isPickup: false, address: destinationAddress }
    ];

    item.polylinePath = [origin, ...waypointCoords, destination];
    const mapPoints = [origin, ...waypointCoords, destination];
    item.mapCenter = {
      lat: mapPoints.reduce((sum, p) => sum + p.lat, 0) / mapPoints.length,
      lng: mapPoints.reduce((sum, p) => sum + p.lng, 0) / mapPoints.length
    };

    item.showMap = true;
    this.fetchRealRoute(origin, destination, waypoints, item).then(() => {
      item.isLoading = false;
      item.showMap = true;
      this.cdr.detectChanges();
    });
  });
}

  fetchRealRoute(
    origin: google.maps.LatLngLiteral,
    destination: google.maps.LatLngLiteral,
    waypoints: google.maps.DirectionsWaypoint[],
    item: any
  ): Promise<void> {

    const service = new google.maps.DirectionsService();
    return new Promise((resolve) => {
      service.route(
        { origin, destination, waypoints, optimizeWaypoints: false, travelMode: google.maps.TravelMode.DRIVING },
        (result, status) => {
          if (status === "OK" && result) {
            item.polylinePath = result.routes[0].overview_path.map(p => ({
              lat: p.lat(), lng: p.lng()
            }));
          }
          resolve();
        }
      );
    });
  }

  private getRouteStops(item: any): NormalizedRouteStop[] {
    const rawStops: RouteStopLike[] = Array.isArray(item?.routeStops)
      ? item.routeStops
      : Array.isArray(item?.details)
        ? item.details
        : [];

    const normalized = rawStops.map((stop) => {
      const stopageType = Number(stop?.stopageType);
      const location = stop?.location || stop?.addressInfo || stop?.addressId || {};
      const lat = Number(location?.lat);
      const lng = Number((location as any)?.lng ?? location?.long);
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(stopageType)) {
        return null;
      }
      return {
        stopageType,
        position: { lat, lng },
        address: this.buildAddress(stop)
      };
    }).filter((stop): stop is NormalizedRouteStop => !!stop);

    if (normalized.length >= 2) {
      return normalized;
    }

    const oLat = Number(item?.originCords?.lat);
    const oLng = Number(item?.originCords?.lng);
    const dLat = Number(item?.destinationCords?.lat);
    const dLng = Number(item?.destinationCords?.lng);
    const fallback: NormalizedRouteStop[] = [];
    if (Number.isFinite(oLat) && Number.isFinite(oLng)) {
      fallback.push({ stopageType: 1, position: { lat: oLat, lng: oLng }, address: item?.origin || '' });
    }
    if (Number.isFinite(dLat) && Number.isFinite(dLng)) {
      fallback.push({ stopageType: 5, position: { lat: dLat, lng: dLng }, address: item?.destination || '' });
    }
    return fallback;
  }

  // Full address string, built exactly like the load-detail popup (app-load-route-markers).
  private buildAddress(stop: any): string {
    const addr = { ...stop?.addressId, ...stop?.addressInfo, ...stop?.location };
    return [addr.address, addr.city, addr.state, addr.zipCode || addr.postalCode, addr.country]
      .filter((part) => part && String(part).trim())
      .join(', ');
  }

  private isPickupStop(stopageType: number): boolean {
    return stopageType === 1 || stopageType === 2;
  }

  private isDropStop(stopageType: number): boolean {
    return stopageType === 3 || stopageType === 4 || stopageType === 5;
  }

  private markerOptions(color: string, title?: string): google.maps.MarkerOptions {
    const width = 32;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="42" viewBox="0 0 ${width} 42">` +
      `<path d="M16 1C7.7 1 1 7.7 1 16c0 10 15 25 15 25s15-15 15-25C31 7.7 24.3 1 16 1z" fill="${color}" stroke="#fff" stroke-width="2"/>` +
      `<circle cx="16" cy="16" r="5" fill="#fff"/>` +
      `</svg>`;
    return {
      icon: {
        url: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg),
        scaledSize: { width, height: 42 } as google.maps.Size,
        anchor: { x: 16, y: 41 } as google.maps.Point
      },
      title
    };
  }

  ngAfterViewChecked() {

  }

}
